import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Copy, Check, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";

const THEMES = [
  { key: "default", label: "Forest", swatch: "#2C5530" },
  { key: "dark", label: "Midnight", swatch: "#0F172A" },
  { key: "light", label: "Ivory", swatch: "#FFFFFF" },
  { key: "mono", label: "Mono", swatch: "#1C1917" },
];

export default function BadgeEmbed() {
  const [slug, setSlug] = useState(null);
  const [theme, setTheme] = useState("default");
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api.get("/badge/me").then((r) => setSlug(r.data.badge_slug));
  }, []);

  if (!slug) return null;

  const svgUrl = `${API}/badge/${slug}.svg${theme !== "default" ? `?theme=${theme}` : ""}`;
  const publicPage = `${window.location.origin}/badge/${slug}`;
  const htmlSnippet = `<a href="${publicPage}" target="_blank" rel="noopener">\n  <img src="${svgUrl}" alt="Net-zero powered by Carbon Vaults" width="320" height="88"/>\n</a>`;
  const markdownSnippet = `[![Net-zero powered by Carbon Vaults](${svgUrl})](${publicPage})`;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(null), 2500);
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 mt-8">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#D97757]"/>
        <div className="text-sm font-medium">Retirement badge</div>
      </div>
      <p className="text-sm text-stone-600 mb-5">Live-updating widget — pick a theme that matches your website.</p>

      <div className="flex flex-wrap gap-2 mb-5">
        {THEMES.map((t) => (
          <button
            key={t.key}
            onClick={() => setTheme(t.key)}
            data-testid={`badge-theme-${t.key}`}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border ${theme === t.key ? "border-[#2C5530] bg-[#2C5530]/5 text-[#2C5530]" : "border-stone-200 bg-white"}`}
          >
            <span className="inline-block w-3 h-3 rounded-full border border-stone-300" style={{ background: t.swatch }}/>
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Live preview</div>
          <div className={`p-4 rounded-xl flex items-center justify-center ${theme === "light" ? "bg-stone-200" : "bg-stone-100"}`}>
            <img data-testid="badge-preview" src={svgUrl} alt="Net-zero badge" className="max-w-full"/>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase tracking-widest text-stone-500">HTML</div>
              <Button size="sm" variant="ghost" onClick={() => copy(htmlSnippet, "html")} data-testid="copy-html">
                {copied === "html" ? <Check className="w-3.5 h-3.5"/> : <Copy className="w-3.5 h-3.5"/>}
              </Button>
            </div>
            <pre className="text-xs bg-stone-50 border border-stone-200 rounded-lg p-3 overflow-x-auto font-mono">{htmlSnippet}</pre>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase tracking-widest text-stone-500">Markdown</div>
              <Button size="sm" variant="ghost" onClick={() => copy(markdownSnippet, "md")} data-testid="copy-md">
                {copied === "md" ? <Check className="w-3.5 h-3.5"/> : <Copy className="w-3.5 h-3.5"/>}
              </Button>
            </div>
            <pre className="text-xs bg-stone-50 border border-stone-200 rounded-lg p-3 overflow-x-auto font-mono">{markdownSnippet}</pre>
          </div>
          <a href={svgUrl} target="_blank" rel="noreferrer" className="text-xs text-[#2C5530] inline-flex items-center gap-1 hover:underline">
            View raw SVG <ExternalLink className="w-3 h-3"/>
          </a>
        </div>
      </div>
    </div>
  );
}
